import { JsonFormsCore, JsonSchema, UISchemaElement } from '@jsonforms/core';
import {
  getProgramActiveVersion,
  getProgramMetadata,
  ProgramMetadata,
  ProgramVersion,
} from '@plentifulapp/plentiful-program-configuration';
import { SaveResult, WizardEngine } from '@plentifulapp/plentiful-wizard-engine';
import { AttestationFacade } from '../services/AttestationFacade';
import { Translations } from '@plentifulapp/plentiful-wizard-engine/types/WizardForm';

/**
 * Encapsulation for dependencies to the generalized state programs.
 * Note that these dependencies are all-or-nothing, so unless all of them are
 * able to be created for a given program, then the program should not be used.
 */
export class StateProgramFacade {
  /**
   * Indicates if the last update to the form was a success or not.
   * The handler does get called when frame first loads,
   * so this should flip to a real state as soon as the form load.
   */
  private lastFrameUpdate?: SaveResult;

  /**
   * Constructs a wrapper for the state program services. Should only be used through the factory.
   * @param programMetadata Metadata that identifies the program and points to the current version.
   * @param programVersion Configuration for the current version of the program being used.
   * @param wizardEngine Engine that manages the current frame/step in a wizard-style form.
   * @param attestationFacade Client for service calls to read and write attestations.
   */
  private constructor(
    private programMetadata: ProgramMetadata,
    private programVersion: ProgramVersion,
    private wizardEngine: WizardEngine,
    private attestationFacade: AttestationFacade
  ) {}

  /**
   * @returns ID for the program that these service instances support.
   */
  public get id() {
    return this.programMetadata.id;
  }

  /**
   * @returns Human-readable name for the program.
   */
  public get name() {
    return this.programMetadata.name;
  }

  /**
   * @returns Current program version being serviced.
   */
  public get version() {
    return this.programMetadata.activeVersion;
  }

  /**
   * @returns Most recent update state from Wizard Form.
   */
  public get frameUpdateStatus() {
    return this.lastFrameUpdate;
  }

  /**
   * @returns JSON Forms compatible JSON schema for the attestation data on the current frame.
   */
  public get frameAttestationSchema(): JsonSchema {
    // convert from the validator's type to the form's type, but should match
    return this.wizardEngine.getCurrentStep().jsonSchema as JsonSchema;
  }

  /**
   * @returns JSON Forms compatible JSON schema for the UI presentation of the frame.
   */
  public get frameUiSchema(): UISchemaElement {
    // convert from the validator's type to the form's type
    // (with a layer in between because it doesn't know that they align)
    return this.wizardEngine.getCurrentStep().uiSchema as unknown as UISchemaElement;
  }

  /**
   * @returns Translation data for all locales supported on the current frame.
   */
  public get frameTranslations(): { [locale: string]: Translations } {
    return this.wizardEngine.getCurrentStep().schemaTranslations;
  }

  /**
   * @returns Form data from current step only.
   */
  public get frameAttestationData() {
    return this.wizardEngine.getStepFormData();
  }

  /**
   * @returns Form data from all steps.
   */
  public get completeAttestationData() {
    return this.wizardEngine.getCompleteFormData();
  }

  public changeFrameData(state: Pick<JsonFormsCore, 'data' | 'errors'>) {
    // save the data
    // (validation errors are automatically passed directly to the controls)
    const update = this.wizardEngine.saveFormData(state.data);
    // persist if the data passed validation before moving on
    this.lastFrameUpdate = update;
  }

  /**
   * Submits an attestation to this program.
   * @param neighborUserId User ID of the neighbor making the attestation.
   * @param visitId Registered visit that the attestations is submitted to.
   * @param isComplete True iff the neighbor got through all of the questions before saving.
   */
  public async attest(neighborUserId: number, visitId: number, isComplete: boolean): Promise<void> {
    return await this.attestationFacade.submit(
      neighborUserId,
      visitId,
      this.programMetadata.id,
      this.programVersion.version,
      this.completeAttestationData,
      isComplete
    );
  }

  /**
   * Build state program services for the specific program.
   * @param programId The ID of the state program to use.
   * @param programMetadataLoader Provider function for program metadata.
   * @param programVersionLoader Provider function for program version config.
   * @param wizardEngineFactory Constructor function for wizard engine.
   * @param attestationFacade Instance of the Attestation Facade for the current environment.
   * @returns Object that encapsulates all of the services for this program.
   */
  public static buildForProgram(
    programId: string,
    programMetadataLoader: typeof getProgramMetadata,
    programVersionLoader: typeof getProgramActiveVersion,
    wizardEngineFactory: typeof WizardEngine,
    attestationFacade: AttestationFacade
  ): StateProgramFacade {
    const programMetadata = programMetadataLoader(programId);
    const programVersion = programVersionLoader(programId);
    const wizardEngine = new wizardEngineFactory(programVersion.wizardForm);
    return new StateProgramFacade(programMetadata, programVersion, wizardEngine, attestationFacade);
  }
}
